import React, { useContext, useMemo } from 'react';
import { isEmpty, isNil, isUndefined } from 'lodash';
import { SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { getColumnLetter } from 'utilities/alphabet-utilities';
import { CellProps } from './Cell.props';
import { CellAttributes, FeaturedSpreadsheetContextType } from './Cell.types';

const EXPANDED_STATES = [USER_EXPAND, SYSTEM_EXPAND];

const DisplayCell = (props: CellProps) => {
  const {
    attributesRenderer,
    cell,
    children,
    className,
    col,
    onContextMenu,
    onFocus,
    onMouseDown,
    onMouseOver,
    row,
    style: datasheetStyle,
  } = props;

  const {
    colSpan,
    isChildWithDivider,
    isParent,
    isParentWithDivider,
    needsSpacer,
    isVisibleColumn,
    parentColumn,
    rowSpan,
  } = cell;

  const {
    collapsibleColumns = {},
    displayLegend = true,
    isReadOnly,
    rowGroups,
  } = useContext(FeaturedSpreadsheetContext) as unknown as FeaturedSpreadsheetContextType;

  const attributes: CellAttributes = useMemo(
    () => (attributesRenderer ? attributesRenderer(cell, row, col) : {}),
    [attributesRenderer, cell, row, col]
  );

  const classes = useMemo(() => {
    let isVisible = false;
    if (!isNil(isVisibleColumn)) {
      isVisible = isVisibleColumn;
    } else {
      isVisible = isParent || isUndefined(collapsibleColumns) || !collapsibleColumns[parentColumn as string];
    }
    const hideLegend = !displayLegend && cell.isLegend;
    let cellClassName = className ?? '';
    if (isReadOnly && !className?.includes('read-only')) {
      cellClassName += ' read-only';
    }

    return [
      cellClassName,
      isVisible && !hideLegend ? 'visible' : 'hidden',
      isChildWithDivider && isVisible ? 'child-with-divider' : '',
      isParentWithDivider ? 'parent-with-divider' : '',
      needsSpacer && isVisible ? 'projection-to-ltm-divider' : '',
    ].join(' ');
  }, [
    cell.isLegend,
    className,
    collapsibleColumns,
    displayLegend,
    isChildWithDivider,
    isParent,
    isParentWithDivider,
    isReadOnly,
    isVisibleColumn,
    needsSpacer,
    parentColumn,
  ]);

  const dataCellKey = attributes.id ?? `CELL-${getColumnLetter(col)}${row}`;

  const dataRowExpanded = useMemo(() => {
    const { alias = '', titleAlias = '', hasSubRows, showSubRows } = cell;
    if (!hasSubRows || !showSubRows || isEmpty(rowGroups)) {
      return false;
    }

    const rowState = rowGroups?.[alias] ?? rowGroups?.[titleAlias];
    return EXPANDED_STATES.includes(rowState);
  }, [cell, rowGroups]);

  const cellStyle = useMemo(() => {
    const merged = {
      ...(cell.style || {}),
      ...(datasheetStyle || {}),
    };
    return Object.keys(merged).length ? merged : undefined;
  }, [cell.style, datasheetStyle]);

  return (
    <td
      className={classes}
      colSpan={colSpan || undefined}
      data-cell-id={dataCellKey}
      data-row-expanded={dataRowExpanded}
      data-row-span={rowSpan}
      onContextMenu={onContextMenu}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      onMouseOver={onMouseOver}
      rowSpan={rowSpan || undefined}
      {...attributes}
      style={cellStyle || undefined}>
      {cell.dragHandle}
      {cell.resizeHandle}
      {children}
    </td>
  );
};

DisplayCell.defaultProps = {
  selected: false,
  editing: false,
  updated: false,
  attributesRenderer: () => ({}),
};

export default DisplayCell;
